import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import DocumentoDialog, { conVistaPrevia, useVistaPdf } from './DocumentoDialog';
import EnviarDocumento, { Canal, Envio } from './EnviarDocumento';

interface Visita { km: number | null }
interface Resumen {
  periodo: string;
  cliente: { nombre: string; email: string; telefono: string; contacto: string };
  unidades: { id: string; visitas: Visita[] }[];
  sinUnidad: Visita[];
}

const MESES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

/** Últimos 12 meses, del más nuevo al más viejo ('YYYY-MM') */
const ultimosMeses = () => {
  const hoy = new Date();
  return Array.from({ length: 12 }, (_, i) => {
    const d = new Date(hoy.getFullYear(), hoy.getMonth() - i, 1);
    return { ym: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`, label: `${MESES[d.getMonth()]} ${d.getFullYear()}` };
  });
};

/** Los primeros días del mes se suele mandar el mes anterior */
const mesPorDefecto = () => {
  const hoy = new Date();
  const d = hoy.getDate() <= 10 ? new Date(hoy.getFullYear(), hoy.getMonth() - 1, 1) : hoy;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

/**
 * Resumen mensual de flota: lo que se le hizo a cada unidad del cliente en el
 * mes, con los km. Se genera a mano desde la ficha del cliente.
 */
const ResumenFlotaDialog: React.FC<{
  clientId: string | null;
  clientName?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}> = ({ clientId, clientName, open, onOpenChange }) => {
  const meses = useMemo(ultimosMeses, []);
  const [mes, setMes] = useState(mesPorDefecto());
  const [conImportes, setConImportes] = useState(false);
  const [resumen, setResumen] = useState<Resumen | null>(null);
  const [envios, setEnvios] = useState<Envio[]>([]);
  const [error, setError] = useState<string | null>(null);
  const vista = useVistaPdf();
  const { generar, limpiar } = vista;

  const pedirPdf = useCallback((m: string, conImp: boolean) => () =>
    axios.post(`/api/informes/cliente/${clientId}/resumen/pdf`, { mes: m, conImportes: conImp }, { responseType: 'blob' }).then((r) => r.data as Blob),
  [clientId]);

  useEffect(() => { if (!open) { setResumen(null); setMes(mesPorDefecto()); setConImportes(false); limpiar(); } }, [open, limpiar]);

  // Datos del mes (para los números y el envío)
  useEffect(() => {
    if (!open || !clientId) return;
    let vivo = true;
    setError(null);
    setResumen(null);
    axios.get(`/api/informes/cliente/${clientId}/resumen`, { params: { mes } })
      .then(({ data }) => { if (vivo) { setResumen(data.resumen); setEnvios(data.envios || []); } })
      .catch((e) => { if (vivo) setError(e.response?.data?.error || 'No se pudo armar el resumen'); });
    return () => { vivo = false; };
  }, [open, clientId, mes]);

  // Vista previa: se rehace al cambiar el mes o los importes
  useEffect(() => {
    if (open && clientId && conVistaPrevia()) generar(pedirPdf(mes, conImportes));
  }, [open, clientId, mes, conImportes, generar, pedirPdf]);

  const atendidas = resumen ? resumen.unidades.filter((u) => u.visitas.length) : [];
  const visitas = atendidas.reduce((s, u) => s + u.visitas.length, 0);
  const sinKm = atendidas.reduce((s, u) => s + u.visitas.filter((v) => !v.km).length, 0);
  const vacio = !!resumen && !visitas && !resumen.sinUnidad.length;

  const enviar = async (canal: Canal, destino: string, guardarMail: boolean) => {
    if (!clientId) return;
    try {
      const { data } = await axios.post(`/api/informes/cliente/${clientId}/resumen/enviar`, { mes, conImportes, canal, destino, guardarMail });
      setEnvios(data.envios || []);
      toast.success(canal === 'mail' ? `Resumen enviado a ${destino}` : 'Resumen enviado por WhatsApp');
    } catch (e: any) {
      toast.error(e.response?.data?.error || 'No se pudo enviar el resumen');
    }
  };

  return (
    <DocumentoDialog
      open={open}
      onOpenChange={onOpenChange}
      titulo="Resumen mensual"
      subtitulo={resumen?.cliente.nombre || clientName || ''}
      vista={vista}
      archivo={`Resumen_${mes}.pdf`}
      pedirPdf={pedirPdf(mes, conImportes)}
      puedeAbrir={!!resumen && !vacio}
      pie={resumen && !vacio && (
        <EnviarDocumento
          mailInicial={resumen.cliente.email}
          telefonoInicial={resumen.cliente.telefono}
          ofrecerGuardarMail={!resumen.cliente.email}
          envios={envios}
          onEnviar={enviar}
        />
      )}
    >
      <section>
        <div className="mb-1.5 text-xs font-medium text-muted-foreground">Mes</div>
        <Select value={mes} onValueChange={setMes}>
          <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
          <SelectContent>
            {meses.map((m) => <SelectItem key={m.ym} value={m.ym}>{m.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <p className="mt-1.5 text-xs text-muted-foreground">
          {error ? <span className="text-destructive">{error}</span>
            : !resumen ? 'Cargando…'
            : vacio ? `No hay remitos emitidos en ${resumen.periodo.toLowerCase()}.`
            : `${atendidas.length} de ${resumen.unidades.length} unidades · ${visitas} ${visitas === 1 ? 'visita' : 'visitas'}${resumen.sinUnidad.length ? ` · ${resumen.sinUnidad.length} sin unidad` : ''}`}
        </p>
        {!!sinKm && <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">{sinKm} {sinKm === 1 ? 'visita sale' : 'visitas salen'} sin km (remitos de antes de que el km fuera obligatorio).</p>}
      </section>

      <label className="flex cursor-pointer items-center justify-between gap-3">
        <span>
          <span className="block text-sm font-medium">Incluir importes</span>
          <span className="block text-xs text-muted-foreground">Importe por visita y total del mes</span>
        </span>
        <Switch checked={conImportes} onCheckedChange={setConImportes} />
      </label>
    </DocumentoDialog>
  );
};

export default ResumenFlotaDialog;
